/**
 * Iter 65b Section 4 — 1-click unsubscribe confirmation page.
 *
 * Public route (no auth) reached via `/unsubscribe?email=...&type=...&token=...`
 * from the email footer link. Calls the backend GET /api/unsubscribe which
 * verifies the HMAC token and flips the user\u2019s preference to off across
 * every workspace they belong to.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    const email = params.get('email') || '';
    const type = params.get('type') || '';
    const token = params.get('token') || '';
    if (!email || !type || !token) {
      setState({ status: 'error', message: 'Missing or invalid unsubscribe link.' });
      return;
    }
    (async () => {
      try {
        const url = `${API_URL}/unsubscribe?email=${encodeURIComponent(email)}&type=${encodeURIComponent(type)}&token=${encodeURIComponent(token)}`;
        const r = await fetch(url, { method: 'GET' });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          setState({
            status: r.status === 401 ? 'invalid' : 'error',
            message: body.detail || `HTTP ${r.status}`,
          });
          return;
        }
        const body = await r.json();
        setState({ status: 'done', body });
      } catch (e) {
        setState({ status: 'error', message: e.message || 'Network error' });
      }
    })();
  }, [params]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background px-4 py-10"
      data-testid="unsubscribe-page"
    >
      <div className="w-full max-w-md rounded-xl border border-border/30 bg-card/70 backdrop-blur p-8 shadow-xl">
        <div className="flex justify-center mb-6">
          <div className="text-gold text-2xl font-bold tracking-wide" data-testid="unsubscribe-brand">
            STELLAR PRODUCTIONS
          </div>
        </div>

        {state.status === 'loading' && (
          <div className="text-center" data-testid="unsubscribe-loading">
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-gold" />
            <p className="text-[12px] text-muted-foreground/60 mt-3">Updating your preferences\u2026</p>
          </div>
        )}

        {state.status === 'done' && (
          <div className="text-center" data-testid="unsubscribe-success">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-400" />
            <h1 className="text-xl font-semibold mt-4">You\u2019re unsubscribed</h1>
            <p className="text-[13px] text-muted-foreground/70 mt-2 leading-relaxed">
              We\u2019ll stop sending you{' '}
              <span className="text-foreground font-medium">{state.body?.label || 'these emails'}</span>{' '}
              {state.body?.workspaces_updated > 1
                ? `across all ${state.body.workspaces_updated} of your workspaces.`
                : 'going forward.'}
            </p>
            <p className="text-[11px] text-muted-foreground/50 mt-3">
              Changed your mind? You can re-enable any email type from{' '}
              <Link
                to="/settings?tab=notifications"
                className="text-gold underline"
                data-testid="unsubscribe-settings-link"
              >
                Settings \u2192 Notifications
              </Link>
              .
            </p>
          </div>
        )}

        {state.status === 'invalid' && (
          <div className="text-center" data-testid="unsubscribe-invalid">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-400" />
            <h1 className="text-lg font-semibold mt-4">Link expired or tampered with</h1>
            <p className="text-[13px] text-muted-foreground/70 mt-2 leading-relaxed">
              This unsubscribe link is no longer valid. To turn off this email, sign in and visit{' '}
              <Link to="/settings?tab=notifications" className="text-gold underline">
                Settings \u2192 Notifications
              </Link>
              .
            </p>
          </div>
        )}

        {state.status === 'error' && (
          <div className="text-center" data-testid="unsubscribe-error">
            <AlertTriangle className="h-12 w-12 mx-auto text-red-400" />
            <h1 className="text-lg font-semibold mt-4">Something went wrong</h1>
            <p className="text-[13px] text-muted-foreground/70 mt-2">
              {state.message || 'Please try again, or contact support@stellarproductions.ca.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
